import { Pagination, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import SortMenu from 'components/common/SortMenu'
import DashboardServiceListCard from 'components/reliability-management/company-dashboard/DashboardServiceListCard'
import { usePagination } from 'hooks/use-pagination'
import {
  DEFAULT_SORT_CATEGORY,
  SortOrder,
  useStoredSorts
} from 'hooks/use-stored-sorts'
import { CompanyReliabilityReportResponse } from 'types/api/policies/reliability-report'
import { naturalSortBy } from 'utils/list'

const DEFAULT_SORT_ORDERS: Record<string, SortOrder> = {
  score: 'desc',
  serviceName: 'asc',
  lastModified: 'desc'
}

const defaultSorts = {
  [DEFAULT_SORT_CATEGORY]: {
    field: 'score',
    order: 'desc'
  }
}

const DashboardServiceList: React.FC<{
  report: CompanyReliabilityReportResponse
}> = ({ report }) => {
  // keep this in session storage so we persist these selections
  // and we don't have to juggle these options in the url along with the page #
  const [sorts, setSorts] = useStoredSorts(
    'companyDashboardServiceListSort',
    defaultSorts
  )

  const sortedServices = useMemo(() => {
    if (report?.services?.length < 1) {
      return []
    }

    return naturalSortBy(
      report.services,
      (service) => service[sorts[DEFAULT_SORT_CATEGORY].field],
      sorts[DEFAULT_SORT_CATEGORY].order
    )
  }, [report.services, sorts])

  const SERVICES_PER_PAGE = 10
  const { pagination } = usePagination(
    sortedServices,
    SERVICES_PER_PAGE,
    0,
    false
  )

  return (
    <Stack data-test-company-dashboard-service-list direction="column" gap={2}>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography data-test-title variant="h6">
          All Services ({report?.services?.length})
        </Typography>
        <SortMenu
          menuItems={[
            { value: 'score', label: 'Score' },
            { value: 'serviceName', label: 'Name' },
            { value: 'lastModified', label: 'Modified Date' }
          ]}
          sortField={sorts[DEFAULT_SORT_CATEGORY].field}
          sortOrder={sorts[DEFAULT_SORT_CATEGORY].order}
          onSortMenuChange={(value) => {
            setSorts({
              ...sorts,
              [DEFAULT_SORT_CATEGORY]: {
                field: value,
                order: DEFAULT_SORT_ORDERS[value]
              }
            })
            // reset pagination on sort change
            pagination.changePage(0)
          }}
          onSortOrderClick={() => {
            setSorts({
              ...sorts,
              [DEFAULT_SORT_CATEGORY]: {
                field: sorts[DEFAULT_SORT_CATEGORY].field,
                order:
                  sorts[DEFAULT_SORT_CATEGORY].order === 'desc' ? 'asc' : 'desc'
              }
            })
            // reset pagination on sort change
            pagination.changePage(0)
          }}
        />
      </Stack>
      <Typography>
        These are all the Services defined within your company
      </Typography>
      <Stack direction="column" gap={2}>
        {pagination.subset.map((s) => (
          <DashboardServiceListCard key={s.serviceId} service={s} />
        ))}

        {pagination.pageCount > 1 && (
          <Pagination
            data-test-pagination
            count={pagination.pageCount}
            page={pagination.selectedPage + 1}
            sx={{ alignSelf: 'center' }}
            onChange={(_event, newPage) => {
              pagination.changePage(newPage - 1)
            }}
          />
        )}
      </Stack>
    </Stack>
  )
}

export default DashboardServiceList
